import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Battery, Sun, Moon, Dumbbell, Brain, Calendar, CheckCircle2, Sparkles } from 'lucide-react';
import ContactForm from '../components/ContactForm';

const B2CPage = () => {
  const fadeUp = {
    initial: { opacity: 0, y: 50 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-100px" },
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const staggerItem = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const benefits = [
    { icon: Battery, title: 'Énergie stable', description: 'Finies les variations d\'énergie dans la journée' },
    { icon: Brain, title: 'Clarté mentale', description: 'Focus et concentration améliorés' },
    { icon: Sun, title: 'Vitalité quotidienne', description: 'Se sentir bien dès le réveil' },
    { icon: Moon, title: 'Récupération optimale', description: 'Un sommeil vraiment réparateur' },
    { icon: Dumbbell, title: 'Forme physique', description: 'Un corps fonctionnel et performant' },
    { icon: Calendar, title: 'Régularité', description: 'Des habitudes qui tiennent dans le temps' },
  ];

  const targetProfiles = [
    'Vous ressentez régulièrement que vous pourriez être plus clair, plus stable, plus performant',
    'Vous avez essayé plusieurs approches sans résultats durables',
    'Vous cherchez un accompagnement structuré, pas des conseils génériques',
    'Vous êtes prêt(e) à vous engager dans une démarche long terme',
    'Vous voulez des résultats mesurables, pas des promesses',
  ];

  const transformations = [
    { before: 'Fatigue chronique', after: 'Énergie constante' },
    { before: 'Mental dispersé', after: 'Clarté et focus' },
    { before: 'Habitudes instables', after: 'Régularité durable' },
    { before: 'Stress mal géré', after: 'Récupération optimisée' },
  ];

  return (
    <div data-testid="b2c-page" className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden pt-24">
        <div className="absolute inset-0">
          <motion.div
            className="absolute inset-0 z-10"
            animate={{ 
              background: [
                'radial-gradient(ellipse at 20% 50%, rgba(143, 188, 143, 0.15) 0%, rgba(5, 5, 5, 0) 50%)',
                'radial-gradient(ellipse at 40% 30%, rgba(152, 212, 187, 0.15) 0%, rgba(5, 5, 5, 0) 50%)',
                'radial-gradient(ellipse at 20% 50%, rgba(143, 188, 143, 0.15) 0%, rgba(5, 5, 5, 0) 50%)',
              ]
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
          <img
            src="https://images.unsplash.com/photo-1589439200770-d7614e575538?auto=format&fit=crop&w=2000&q=80"
            alt="Silhouette coucher de soleil"
            className="absolute inset-0 w-full h-full object-cover opacity-30"
            onLoad={(e) => e.target.classList.add('loaded')}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/80 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sage/10 border border-sage/20 mb-6"
              >
                <Sparkles size={16} className="text-sage" />
                <span className="font-body text-sm tracking-wide text-sage">
                  Accompagnement Particuliers
                </span>
              </motion.div>
              
              <motion.h1 
                className="font-heading text-5xl md:text-6xl lg:text-7xl font-medium text-white leading-[1.1] mb-6"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
              >
                Retrouvez votre
                <span className="text-sage block">plein potentiel</span>
              </motion.h1>
              
              <motion.p 
                className="font-body text-lg text-gray-300 mb-8 leading-relaxed max-w-lg"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.8 }}
              >
                Vous fonctionnez encore. Mais vous sentez que vous pourriez être 
                plus clair, plus stable, plus performant. Ce n'est pas un problème, 
                c'est un signal.
              </motion.p>

              <motion.div 
                className="flex flex-col sm:flex-row gap-4 mb-8"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.8 }}
              >
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link
                    to="/contact"
                    data-testid="b2c-hero-cta"
                    className="bg-sage text-black font-body font-semibold px-8 py-4 rounded-full hover:bg-sage-300 transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    Parlons de votre situation
                    <ArrowRight size={20} />
                  </Link>
                </motion.div>
              </motion.div>

              <motion.p 
                className="font-body text-sm text-gray-500"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9 }}
              >
                Premier échange de 30 minutes • Réponse sous 24h
              </motion.p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9, x: 50 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="hidden lg:block"
            >
              <motion.div 
                className="relative"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.5 }}
              >
                <img
                  src="https://images.unsplash.com/photo-1765490526034-65d6fa69ab13?auto=format&fit=crop&w=800&q=80"
                  alt="Pierres équilibrées zen"
                  className="rounded-3xl w-full h-[500px] object-cover"
                  onLoad={(e) => e.target.classList.add('loaded')}
                />
                <motion.div 
                  className="absolute -bottom-6 -left-6 glass rounded-2xl p-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  <p className="font-body text-sm text-gray-400 mb-2">Ce qui change</p>
                  <p className="font-heading text-2xl text-sage">Performance durable</p>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="max-w-3xl">
            <span className="font-body text-sm tracking-[0.2em] uppercase text-sage mb-4 block">
              Le constat
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-8">
              La plupart des approches "bien-être" et "performance" échouent.
            </h2>
            <p className="font-body text-lg text-gray-400 leading-relaxed mb-6">
              Programmes standards, actions ponctuelles, coaching isolé, workshops sans suivi... 
              Ces solutions ne traitent pas les véritables leviers de votre fonctionnement quotidien.
            </p>
            <motion.p 
              className="font-body text-lg text-white leading-relaxed"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              La vraie transformation demande une approche globale : 
              corps, énergie, mental et habitudes de vie alignés.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-24 md:py-32 bg-[#050505] relative overflow-hidden">
        <motion.div
          className="absolute -left-32 top-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full bg-sage/5 blur-[100px]"
          animate={{ x: [0, 50, 0] }}
          transition={{ duration: 15, repeat: Infinity }}
        />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="font-body text-sm tracking-[0.2em] uppercase text-sage mb-4 block">
              Ce qui change
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-white mb-6">
              Les résultats observables
            </h2>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                variants={staggerItem}
                whileHover={{ y: -8, scale: 1.02 }}
                className="p-8 rounded-2xl bg-[#0F0F0F] border border-white/5 hover:border-sage/20 transition-all duration-500 group"
              >
                <motion.div 
                  className="w-12 h-12 rounded-xl bg-sage/10 flex items-center justify-center mb-6 group-hover:bg-sage/20 transition-colors"
                  whileHover={{ rotate: 10 }}
                >
                  <benefit.icon className="text-sage" size={24} />
                </motion.div>
                <h3 className="font-heading text-xl text-white mb-3">
                  {benefit.title}
                </h3>
                <p className="font-body text-gray-400">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Transformation Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp}>
              <span className="font-body text-sm tracking-[0.2em] uppercase text-sage mb-4 block">
                La transformation
              </span>
              <h2 className="font-heading text-4xl md:text-5xl text-white mb-8">
                Avant / Après
              </h2>
              
              <motion.div 
                className="space-y-6"
                variants={staggerContainer}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
              >
                {transformations.map((item, index) => (
                  <motion.div
                    key={item.before}
                    variants={staggerItem}
                    className="flex items-center gap-6"
                  >
                    <div className="flex-1 p-4 rounded-xl bg-white/5 border border-white/10">
                      <p className="font-body text-gray-400 line-through">{item.before}</p>
                    </div>
                    <motion.div
                      animate={{ x: [0, 5, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      <ArrowRight className="text-sage flex-shrink-0" size={20} />
                    </motion.div>
                    <div className="flex-1 p-4 rounded-xl bg-sage/10 border border-sage/30">
                      <p className="font-body text-sage">{item.after}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              {...fadeUp}
              transition={{ delay: 0.2 }}
            >
              <motion.img
                src="https://images.unsplash.com/photo-1748859221059-dff6271a3ec3?auto=format&fit=crop&w=800&q=80"
                alt="Montagnes abstraites"
                className="rounded-3xl w-full h-[500px] object-cover"
                onLoad={(e) => e.target.classList.add('loaded')}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.5 }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Target Profile Section */}
      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="max-w-3xl mx-auto text-center mb-16">
            <span className="font-body text-sm tracking-[0.2em] uppercase text-sage mb-4 block">
              Un engagement mutuel
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-white mb-6">
              C'est pour vous si...
            </h2>
          </motion.div>

          <motion.div 
            className="max-w-3xl mx-auto"
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            <ul className="space-y-4">
              {targetProfiles.map((profile, index) => (
                <motion.li
                  key={index}
                  variants={staggerItem}
                  whileHover={{ x: 8 }}
                  className="flex items-start gap-4 p-6 rounded-2xl bg-[#0F0F0F] border border-white/5 hover:border-sage/20 transition-all duration-300"
                >
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity, delay: index * 0.2 }}
                  >
                    <CheckCircle2 className="text-sage flex-shrink-0 mt-0.5" size={24} />
                  </motion.div>
                  <p className="font-body text-white">{profile}</p>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          <motion.div {...fadeUp} className="mt-12 text-center">
            <p className="font-body text-sm text-gray-500 italic max-w-xl mx-auto">
              "Je n'accompagne pas tout le monde. Les places sont volontairement limitées 
              pour garantir un engagement réel et la qualité du suivi."
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div {...fadeUp}>
              <span className="font-body text-sm tracking-[0.2em] uppercase text-sage mb-4 block">
                Prochaine étape
              </span>
              <h2 className="font-heading text-4xl md:text-5xl text-white mb-6">
                Parlons de votre situation
              </h2>
              <p className="font-body text-gray-400 mb-8 leading-relaxed">
                Un premier échange simple et direct pour comprendre où vous en êtes, 
                ce que vous cherchez, et déterminer si nous pouvons travailler ensemble.
              </p>
              
              <motion.div 
                className="space-y-4 mb-8"
                variants={staggerContainer}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
              >
                {[
                  { num: '1', text: 'Vous remplissez le formulaire' },
                  { num: '2', text: 'Je vous recontacte sous 24h' },
                  { num: '3', text: 'On planifie un call de 30 minutes' },
                ].map((step, index) => (
                  <motion.div 
                    key={step.num}
                    variants={staggerItem}
                    className="flex items-center gap-3"
                  >
                    <motion.span 
                      className="w-8 h-8 rounded-full bg-sage/20 flex items-center justify-center text-sage font-body text-sm"
                      whileHover={{ scale: 1.1, backgroundColor: 'rgba(143, 188, 143, 0.3)' }}
                    >
                      {step.num}
                    </motion.span>
                    <p className="font-body text-white">{step.text}</p>
                  </motion.div>
                ))}
              </motion.div>

              <p className="font-body text-sm text-gray-500">
                Sans engagement • Confidentiel
              </p>
            </motion.div>

            <ContactForm
              contactType="b2c"
              title="Votre demande"
              subtitle="Décrivez brièvement votre situation actuelle et vos objectifs."
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default B2CPage;
